import { beginSso } from './sso';
import { GoogleGlyph } from './GoogleGlyph';
import { MicrosoftGlyph } from './MicrosoftGlyph';
import './SsoButtons.css';

interface SsoButtonsProps {
  // The parent's error setter — clicking clears it, and a failed `beginSso`
  // (no Web Crypto / locked storage) surfaces retry copy through it, so the
  // message lands in whatever error slot the host page already renders.
  setError: (message: string) => void;
  // Disable both while the host page is mid-submit (e.g. the invite page's
  // password accept in flight), so a click can't start a second, racing flow.
  disabled?: boolean;
}

/**
 * The two "Continue with <provider>" SSO controls (Google SC-2621, Microsoft
 * SC-2720), shared by the login page and the SC-2791 invite-accept page.
 *
 * Each click mints + stashes a per-tab client nonce (SC-2780) then does a
 * full-page browser nav to the BE authorize endpoint — NOT a fetch (it
 * 307-redirects to the provider's consent screen, which CORS can't follow).
 * These are <button>s, not <a href>s: a per-tab nonce can't survive "open in
 * new tab" (fresh tab = empty sessionStorage), so we don't expose a bypassable
 * href that would start a flow the callback can never match. If `beginSso`
 * throws (no Web Crypto / locked storage) we surface a retry instead of
 * navigating into that unmatchable flow or leaving a dead control.
 */
export function SsoButtons({ setError, disabled = false }: SsoButtonsProps) {
  const startSso = (provider: 'google' | 'microsoft') => () => {
    setError('');
    try {
      window.location.assign(beginSso(provider));
    } catch {
      setError('Could not start sign-in. Please try again.');
    }
  };

  return (
    <div className="sso-buttons">
      <button
        type="button"
        className="sso-btn"
        onClick={startSso('google')}
        disabled={disabled}
      >
        <GoogleGlyph />
        Continue with Google
      </button>
      <button
        type="button"
        className="sso-btn"
        onClick={startSso('microsoft')}
        disabled={disabled}
      >
        <MicrosoftGlyph />
        Continue with Microsoft
      </button>
    </div>
  );
}
